import { useEffect, useState } from "react";
import {
  Loader2,
  UserPlus,
  Mail,
  User as UserIcon,
  Crown,
  KeyRound,
  Wand2,
  ShieldAlert,
  Check,
  Copy,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Switch } from "../ui/switch";
import { api, formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

/**
 * Platform Admin's "Yeni Kullanıcı Ekle" dialog scoped to a specific tenant.
 * Posts `POST /platform/tenants/:id/users` with the form values, then
 * surfaces the created plaintext password ONCE (same one-shot pattern
 * as UserPasswordResetDialog) so the admin can hand it off.
 */
export default function TenantAddUserDialog({ open, onOpenChange, tenant, onCreated }) {
  const [phase, setPhase] = useState("form"); // "form" | "reveal"
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("user");
  const [password, setPassword] = useState("");
  const [mustChange, setMustChange] = useState(true);
  const [busy, setBusy] = useState(false);
  const [createdPassword, setCreatedPassword] = useState("");
  const [createdEmail, setCreatedEmail] = useState("");
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (open) {
      setPhase("form");
      setName("");
      setEmail("");
      setRole("user");
      setPassword("");
      setMustChange(true);
      setBusy(false);
      setCreatedPassword("");
      setCreatedEmail("");
      setCopied(false);
    }
  }, [open]);

  if (!tenant) return null;

  const generatePassword = () => {
    // ~13-char password using URL-safe alphabet + a symbol prefix.
    const arr = new Uint8Array(9);
    (window.crypto || window.msCrypto).getRandomValues(arr);
    const base64 = btoa(String.fromCharCode(...arr))
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=+$/, "");
    setPassword("!" + base64.slice(0, 12));
  };

  const submit = async () => {
    if (!name.trim()) {
      toast.error("Ad boş bırakılamaz");
      return;
    }
    if (!email.trim() || !email.includes("@")) {
      toast.error("Geçersiz e-posta");
      return;
    }
    if (password.length < 8) {
      toast.error("Şifre en az 8 karakter olmalı");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/platform/tenants/${tenant.id}/users`, {
        name: name.trim(),
        email: email.trim(),
        role,
        password,
        must_change_password: mustChange,
      });
      setCreatedPassword(password);
      setCreatedEmail(email.trim());
      setPhase("reveal");
      onCreated?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Kullanıcı oluşturulamadı");
    } finally {
      setBusy(false);
    }
  };

  const copyPassword = async () => {
    try {
      await navigator.clipboard.writeText(createdPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const el = document.getElementById("created-password-value");
      if (el) el.select();
    }
  };

  const close = () => {
    // Wipe plaintext from state before closing the modal.
    setCreatedPassword("");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={busy ? undefined : (v) => (v ? null : close())}>
      <DialogContent data-testid="tenant-add-user-dialog">
        {phase === "form" && (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-violet-100">
                <UserPlus className="h-5 w-5 text-violet-700" />
              </div>
              <DialogTitle>Yeni Kullanıcı — {tenant.name}</DialogTitle>
              <DialogDescription>
                Bu tenant altına yeni bir kullanıcı ekle. Kullanıcı aşağıda
                seçtiğin şifre ile giriş yapacak.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs">
                  <UserIcon className="h-3.5 w-3.5 text-zinc-500" /> Ad Soyad
                </Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Ad Soyad"
                  disabled={busy}
                  data-testid="tenant-add-user-name"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs">
                  <Mail className="h-3.5 w-3.5 text-zinc-500" /> E-posta
                </Label>
                <Input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="user@example.com"
                  disabled={busy}
                  data-testid="tenant-add-user-email"
                />
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs">
                  <Crown className="h-3.5 w-3.5 text-zinc-500" /> Rol
                </Label>
                <Select value={role} onValueChange={setRole} disabled={busy}>
                  <SelectTrigger data-testid="tenant-add-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="user">user</SelectItem>
                    <SelectItem value="super_admin">super_admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="flex items-center gap-1.5 text-xs">
                  <KeyRound className="h-3.5 w-3.5 text-zinc-500" /> İlk Şifre
                </Label>
                <div className="flex gap-2">
                  <Input
                    type="text"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Minimum 8 karakter"
                    disabled={busy}
                    data-testid="tenant-add-user-password"
                    className="font-mono"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={generatePassword}
                    disabled={busy}
                    data-testid="tenant-add-user-generate"
                    className="shrink-0"
                  >
                    <Wand2 className="mr-1 h-4 w-4" /> Oluştur
                  </Button>
                </div>
              </div>
              <div className="flex items-start gap-3 rounded-md border border-amber-200 bg-amber-50 p-3">
                <ShieldAlert className="mt-0.5 h-4 w-4 shrink-0 text-amber-700" />
                <div className="flex-1">
                  <div className="text-xs font-semibold text-amber-900">
                    İlk girişte şifresini değiştirmek zorunda olsun
                  </div>
                  <div className="mt-0.5 text-[11px] text-amber-800">
                    Kullanıcı bu şifreyle giriş yapınca uygulama, kendi şifresini
                    seçene kadar başka hiçbir sayfaya girişine izin vermeyecek.
                  </div>
                </div>
                <Switch
                  checked={mustChange}
                  onCheckedChange={setMustChange}
                  disabled={busy}
                  data-testid="tenant-add-user-must-change"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={close}
                disabled={busy}
                data-testid="tenant-add-user-cancel"
              >
                Vazgeç
              </Button>
              <Button
                onClick={submit}
                disabled={busy}
                data-testid="tenant-add-user-submit"
                className="bg-violet-600 text-white hover:bg-violet-700"
              >
                {busy ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Oluşturuluyor…
                  </>
                ) : (
                  <>
                    <UserPlus className="mr-2 h-4 w-4" /> Kullanıcıyı Oluştur
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}

        {phase === "reveal" && (
          <>
            <DialogHeader>
              <div className="mb-2 flex h-11 w-11 items-center justify-center rounded-full bg-emerald-100">
                <Check className="h-5 w-5 text-emerald-700" />
              </div>
              <DialogTitle>Kullanıcı oluşturuldu</DialogTitle>
              <DialogDescription>
                <b>{createdEmail}</b> için hesap oluşturuldu. Aşağıdaki
                bilgileri kullanıcıya iletin — şifreyi <b>bu ekranı kapatınca</b>{" "}
                bir daha görüntüleyemezsiniz.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  E-posta
                </div>
                <div className="mt-0.5 font-mono text-sm text-zinc-900">
                  {createdEmail}
                </div>
              </div>
              <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-500">
                  Şifre
                </div>
                <div className="mt-1 flex gap-2">
                  <Input
                    id="created-password-value"
                    value={createdPassword}
                    readOnly
                    onFocus={(e) => e.target.select()}
                    className="font-mono text-sm"
                    data-testid="tenant-add-user-created-password"
                  />
                  <Button
                    variant="outline"
                    onClick={copyPassword}
                    data-testid="tenant-add-user-copy-password"
                    className="shrink-0"
                  >
                    {copied ? (
                      <>
                        <Check className="mr-1.5 h-4 w-4 text-emerald-600" /> Kopyalandı
                      </>
                    ) : (
                      <>
                        <Copy className="mr-1.5 h-4 w-4" /> Kopyala
                      </>
                    )}
                  </Button>
                </div>
              </div>
              {mustChange && (
                <div className="rounded-md border border-violet-200 bg-violet-50 px-3 py-2 text-[11px] text-violet-800">
                  Kullanıcı ilk girişte kendi şifresini seçmek zorunda olacak.
                </div>
              )}
            </div>
            <DialogFooter>
              <Button
                onClick={close}
                data-testid="tenant-add-user-done"
                className="bg-zinc-900 text-white hover:bg-zinc-800"
              >
                Kapat
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
